import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Landing } from './pages/Landing';
import { Download } from './pages/Download';
import { SignUp } from './pages/SignUp';
import { SignIn } from './pages/SignIn';
import { EmailConfirmation } from './pages/EmailConfirmation';
import { PasswordReset } from './pages/PasswordReset';
import { PasswordResetConfirm } from './pages/PasswordResetConfirm';
import { Invite } from './pages/Invite';
import { Dashboard } from './pages/Dashboard';
import { Projects } from './pages/Projects';
import { ProjectCanvas } from './pages/ProjectCanvas';
import { Documents } from './pages/Documents';
import { DocumentEditor } from './pages/DocumentEditor';
import { WorkItems } from './pages/WorkItems';
import { WorkItemDetail } from './pages/WorkItemDetail';
import { Members } from './pages/Members';
import { Settings } from './pages/Settings';
import { SettingsBilling } from './pages/SettingsBilling';
import { Paywall } from './pages/Paywall';
import { SettingsOrganization } from './pages/SettingsOrganization';
import { SettingsOrganizations } from './pages/SettingsOrganizations';
import { SettingsFunctionRoles } from './pages/SettingsFunctionRoles';
import { SettingsPermissionGroups } from './pages/SettingsPermissionGroups';
import { SettingsDocuments } from './pages/SettingsDocuments';
import { SettingsWorkItems } from './pages/SettingsWorkItems';
import { SettingsOrgStandards } from './pages/SettingsOrgStandards';
import { SettingsTaskStatuses } from './pages/SettingsTaskStatuses';
import { SettingsSkills } from './pages/SettingsSkills';
import { Account } from './pages/Account';
import { PersonalTodos } from './pages/PersonalTodos';
import { RootRedirect } from './pages/RootRedirect';
import { DevPrimitives } from './pages/DevPrimitives';
import { DevShellSkeleton } from './pages/DevShellSkeleton';
import { CreateOrgVMProbe } from './dev/CreateOrgVMProbe';
import { OrgCreateFormProbe } from './dev/OrgCreateFormProbe';
import { OpenItemProbe } from './dev/OpenItemProbe';
import { SettingsOrganizationsVMProbe } from './dev/SettingsOrganizationsVMProbe';
import { TestWysiwygEditor } from './pages/TestWysiwygEditor';
import { TestTablePrimitive } from './pages/TestTablePrimitive';
import { TestHistoryDrawer } from './pages/TestHistoryDrawer';
import { TestCommentsPanel } from './pages/TestCommentsPanel';
import { TestAttachDocumentCover } from './pages/TestAttachDocumentCover';
import { TestAttachWorkItemCover } from './pages/TestAttachWorkItemCover';
import { TestAttachExistingModal } from './pages/TestAttachExistingModal';
import { TestDocumentCoverViewer } from './pages/TestDocumentCoverViewer';
import { TestSavebar } from './pages/TestSavebar';
import { TestBlocks } from './pages/TestBlocks';
import { TestInspectorTabs } from './pages/TestInspectorTabs';
import { TestMarkdownEditor } from './pages/TestMarkdownEditor';
import { TestAgentApprovalOverflow } from './pages/TestAgentApprovalOverflow';
import { TestAgentApprovalPopover } from './pages/TestAgentApprovalPopover';
import { TestAgentCommandApprovalWidth } from './pages/TestAgentCommandApprovalWidth';
import { TestAgentComposerLayout } from './pages/TestAgentComposerLayout';
import { TestAgentWriteScrollJump } from './pages/TestAgentWriteScrollJump';
import { TestAgentChatFollow } from './pages/TestAgentChatFollow';
import { TestAgentWorking } from './pages/TestAgentWorking';
import { TestAgentRouteScroll } from './pages/TestAgentRouteScroll';
import { TestTranscriptRowCompression } from './pages/TestTranscriptRowCompression';
import { TestAgentBlockRow } from './pages/TestAgentBlockRow';
import { TestAgentSubagent } from './pages/TestAgentSubagent';
import { TestComposer } from './pages/TestComposer';
import { TestChipSend } from './pages/TestChipSend';
import { TestSlashPalette } from './pages/TestSlashPalette';
import { TestSkillCard } from './pages/TestSkillCard';
import { TestCreateSkill } from './pages/TestCreateSkill';
import { TestEditorScroll } from './pages/TestEditorScroll';
import { TestProjectCanvasScroll } from './pages/TestProjectCanvasScroll';
import { TestFloatToolbarLayout } from './pages/TestFloatToolbarLayout';
import { SignedInShell } from './components/shell/SignedInShell';
import { RequireAuth } from './components/auth/RequireAuth';

function LegacyTaskDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/work-item-detail/${id ?? ''}`} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/landing" element={<Landing />} />
      <Route path="/download" element={<Download />} />

      {/* Legacy-URL redirects for the Tasks → Work Items + Document Templates → Documents renames. */}
      <Route path="/tasks" element={<Navigate to="/work-items" replace />} />
      <Route path="/task-detail/:id" element={<LegacyTaskDetailRedirect />} />
      <Route
        path="/settings/document-templates"
        element={<Navigate to="/settings/documents" replace />}
      />
      {/* Auth-flow pages — they each own their session-state handling
          (invite hand-off, IDE deep link, signed-in bounce). */}
      <Route path="/sign-up" element={<SignUp />} />
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/email-confirmation" element={<EmailConfirmation />} />
      <Route path="/password-reset" element={<PasswordReset />} />
      <Route path="/password-reset/confirm" element={<PasswordResetConfirm />} />
      <Route path="/invite" element={<Invite />} />

      {import.meta.env.DEV ? (
        <Route path="/dev/primitives" element={<DevPrimitives />} />
      ) : null}
      {import.meta.env.DEV ? (
        <Route path="/dev/shell-skeleton" element={<DevShellSkeleton />} />
      ) : null}
      {import.meta.env.DEV ? (
        <Route path="/dev/create-org-vm" element={<CreateOrgVMProbe />} />
      ) : null}
      {import.meta.env.DEV ? (
        <Route path="/dev/org-create-form" element={<OrgCreateFormProbe />} />
      ) : null}
      {import.meta.env.DEV ? (
        <Route path="/dev/open-item-probe" element={<OpenItemProbe />} />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/dev/settings-organizations-vm"
          element={<SettingsOrganizationsVMProbe />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/wysiwyg-editor"
          element={<TestWysiwygEditor />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/table-primitive"
          element={<TestTablePrimitive />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/history-drawer"
          element={<TestHistoryDrawer />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/comments-panel"
          element={<TestCommentsPanel />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/attach-document-cover"
          element={<TestAttachDocumentCover />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/attach-work-item-cover"
          element={<TestAttachWorkItemCover />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/attach-existing-modal"
          element={<TestAttachExistingModal />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/document-cover-viewer"
          element={<TestDocumentCoverViewer />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route path="/__test/savebar" element={<TestSavebar />} />
      ) : null}
      {import.meta.env.DEV ? (
        <Route path="/__test/blocks" element={<TestBlocks />} />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/inspector-tabs"
          element={<TestInspectorTabs />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/markdown-editor"
          element={<TestMarkdownEditor />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/agent-approval-overflow"
          element={<TestAgentApprovalOverflow />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/agent-approval-popover"
          element={<TestAgentApprovalPopover />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/agent-command-approval-width"
          element={<TestAgentCommandApprovalWidth />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/agent-composer-layout"
          element={<TestAgentComposerLayout />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/float-toolbar-layout"
          element={<TestFloatToolbarLayout />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/agent-write-scroll-jump"
          element={<TestAgentWriteScrollJump />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/agent-chat-follow"
          element={<TestAgentChatFollow />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/agent-working"
          element={<TestAgentWorking />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/editor-scroll"
          element={<TestEditorScroll />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/agent-route-scroll"
          element={<TestAgentRouteScroll />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/project-canvas-scroll"
          element={<TestProjectCanvasScroll />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/transcript-row-compression"
          element={<TestTranscriptRowCompression />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/agent-block-row"
          element={<TestAgentBlockRow />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route
          path="/__test/agent-subagent"
          element={<TestAgentSubagent />}
        />
      ) : null}
      {import.meta.env.DEV ? (
        <Route path="/__test/composer" element={<TestComposer />} />
      ) : null}
      {import.meta.env.DEV ? (
        <Route path="/__test/chip-send" element={<TestChipSend />} />
      ) : null}
      {import.meta.env.DEV ? (
        <Route path="/__test/slash-palette" element={<TestSlashPalette />} />
      ) : null}
      {import.meta.env.DEV ? (
        <Route path="/__test/skill-card" element={<TestSkillCard />} />
      ) : null}
      {import.meta.env.DEV ? (
        <Route path="/__test/create-skill" element={<TestCreateSkill />} />
      ) : null}

      {/* Auth-required pages. RequireAuth gates BEFORE SignedInShell mounts,
          so unauth users never see the dashboard chrome flash. */}
      <Route element={<RequireAuth />}>
        <Route element={<SignedInShell />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:id" element={<ProjectCanvas />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/documents/:id" element={<DocumentEditor />} />
        <Route path="/document-editor/:id" element={<DocumentEditor />} />
        <Route path="/work-items" element={<WorkItems />} />
        <Route path="/work-item-detail/:id" element={<WorkItemDetail />} />
        <Route path="/members" element={<Members />} />
        <Route path="/settings" element={<Settings />} />
        <Route
          path="/settings/billing"
          element={<SettingsBilling />}
        />
        <Route
          path="/settings/organization"
          element={<SettingsOrganization />}
        />
        <Route
          path="/settings/organizations"
          element={<SettingsOrganizations />}
        />
        <Route
          path="/settings/function-roles"
          element={<SettingsFunctionRoles />}
        />
        <Route
          path="/settings/permission-groups"
          element={<SettingsPermissionGroups />}
        />
        <Route
          path="/settings/documents"
          element={<SettingsDocuments />}
        />
        <Route
          path="/settings/role-rules"
          element={<Navigate to="/settings/function-roles" replace />}
        />
        <Route
          path="/settings/org-standards"
          element={<SettingsOrgStandards />}
        />
        <Route path="/settings/skills" element={<SettingsSkills />} />
        <Route
          path="/settings/task-statuses"
          element={<SettingsTaskStatuses />}
        />
        <Route
          path="/settings/work-items"
          element={<SettingsWorkItems />}
        />
        <Route path="/account" element={<Account />} />
        <Route path="/paywall" element={<Paywall />} />
        <Route path="/personal-todos" element={<PersonalTodos />} />
        <Route path="/agent" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}
